import { useEffect, useState } from "preact/hooks";
import type {
  Activity,
  ActivityType,
  Company,
  Contact,
  CrmData,
  Invoice,
  LostReason,
  Opportunity,
  Proposal,
  ProposalStatus,
  Stage,
} from "../types";
import { computeInvoiceTotals } from "../lib/billing";
import { loadData, resetData, saveData } from "./storage";

function genId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

type Listener = () => void;

class Store {
  private state: CrmData = loadData();
  private listeners = new Set<Listener>();

  getState(): CrmData {
    return this.state;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private update(next: (state: CrmData) => CrmData): void {
    this.state = next(this.state);
    saveData(this.state);
    this.listeners.forEach((fn) => fn());
  }

  reset(): void {
    this.state = resetData();
    this.listeners.forEach((fn) => fn());
  }

  addActivity(opportunityId: string, type: ActivityType, content: string, author: string): void {
    const activity: Activity = { id: genId("act"), opportunityId, type, content, author, timestamp: new Date().toISOString() };
    this.update((s) => ({ ...s, activities: [...s.activities, activity] }));
  }

  moveStage(opportunityId: string, stage: Stage, note?: string): void {
    this.update((s) => ({
      ...s,
      opportunities: s.opportunities.map((o) => (o.id === opportunityId ? { ...o, stage } : o)),
    }));
    this.addActivity(opportunityId, "stage-changed", note ?? `Stage changed to ${stage}.`, "System");
  }

  markWon(opportunityId: string, finalValue: number, productId: string, contractStartDate: string): void {
    this.update((s) => ({
      ...s,
      opportunities: s.opportunities.map((o) =>
        o.id === opportunityId ? { ...o, stage: "Won", finalValue, productId, contractStartDate, probability: 100 } : o
      ),
    }));
    this.addActivity(opportunityId, "stage-changed", "Stage changed to Won.", "System");
    this.addActivity(opportunityId, "contract-confirmed", `Contract confirmed, start date ${contractStartDate.slice(0, 10)}.`, "System");
  }

  markLost(opportunityId: string, reason: LostReason): void {
    this.update((s) => ({
      ...s,
      opportunities: s.opportunities.map((o) => (o.id === opportunityId ? { ...o, stage: "Lost", lostReason: reason, probability: 0 } : o)),
    }));
    this.addActivity(opportunityId, "stage-changed", "Stage changed to Lost.", "System");
    this.addActivity(opportunityId, "lost-reason", `Opportunity lost - reason: ${reason}.`, "System");
  }

  updateOpportunity(opportunityId: string, patch: Partial<Opportunity>): void {
    this.update((s) => ({
      ...s,
      opportunities: s.opportunities.map((o) => (o.id === opportunityId ? { ...o, ...patch } : o)),
    }));
  }

  changeOwner(opportunityId: string, newOwner: string): void {
    this.update((s) => ({
      ...s,
      opportunities: s.opportunities.map((o) => (o.id === opportunityId ? { ...o, salesOwner: newOwner } : o)),
    }));
    this.addActivity(opportunityId, "note", `Owner changed to ${newOwner}.`, "System");
  }

  scheduleFollowUp(opportunityId: string, dueDate: string, note: string, author: string): void {
    this.addActivity(opportunityId, "task", `Follow-up scheduled for ${dueDate.slice(0, 10)}: ${note}`, author);
  }

  addOpportunity(input: Omit<Opportunity, "id" | "createdAt" | "tags"> & { tags?: string[] }): Opportunity {
    const opp: Opportunity = { ...input, tags: input.tags ?? [], id: genId("opp"), createdAt: new Date().toISOString() };
    this.update((s) => ({ ...s, opportunities: [...s.opportunities, opp] }));
    this.addActivity(opp.id, "note", "Opportunity created.", opp.salesOwner);
    return opp;
  }

  addCompany(input: Omit<Company, "id" | "createdAt">): Company {
    const company: Company = { ...input, id: genId("c"), createdAt: new Date().toISOString() };
    this.update((s) => ({ ...s, companies: [...s.companies, company] }));
    return company;
  }

  addProposal(input: Omit<Proposal, "id" | "generatedDate">): Proposal {
    const proposal: Proposal = { ...input, id: genId("prop"), generatedDate: new Date().toISOString() };
    this.update((s) => ({ ...s, proposals: [...s.proposals, proposal] }));
    this.addActivity(input.opportunityId, "proposal-sent", `Proposal ${proposal.proposalNumber} created.`, "System");
    return proposal;
  }

  updateProposal(proposalId: string, patch: Partial<Proposal>): void {
    this.update((s) => ({
      ...s,
      proposals: s.proposals.map((p) => (p.id === proposalId ? { ...p, ...patch } : p)),
    }));
  }

  setProposalStatus(proposalId: string, status: ProposalStatus): void {
    const patch: Partial<Proposal> = { status };
    if (status === "Sent") patch.sentDate = new Date().toISOString();
    this.updateProposal(proposalId, patch);
  }

  // ---------- Billing ----------

  addInvoice(input: Omit<Invoice, "id" | "invoiceNumber"> & { invoiceNumber?: string }): Invoice {
    const seq = this.state.invoices.length + 1;
    const invoiceNumber = input.invoiceNumber ?? `INV-${new Date().getFullYear()}-${String(seq).padStart(3, "0")}`;
    const invoice: Invoice = { ...input, invoiceNumber, id: genId("inv") };
    this.update((s) => ({ ...s, invoices: [...s.invoices, invoice] }));
    return invoice;
  }

  updateInvoice(invoiceId: string, patch: Partial<Invoice>): void {
    this.update((s) => ({
      ...s,
      invoices: s.invoices.map((i) => (i.id === invoiceId ? { ...i, ...patch } : i)),
    }));
  }

  sendInvoice(invoiceId: string): void {
    this.updateInvoice(invoiceId, { status: "Sent" });
  }

  voidInvoice(invoiceId: string): void {
    this.updateInvoice(invoiceId, { status: "Void" });
  }

  recordPayment(invoiceId: string, amount: number): void {
    const invoice = this.state.invoices.find((i) => i.id === invoiceId);
    if (!invoice) return;
    const nextPaid = invoice.amountPaid + amount;
    const { total } = computeInvoiceTotals({ ...invoice, amountPaid: 0 });
    const patch: Partial<Invoice> = { amountPaid: nextPaid };
    if (nextPaid >= total) {
      patch.status = "Paid";
      patch.paidDate = new Date().toISOString();
      patch.amountPaid = total;
    } else {
      patch.status = "Partially Paid";
    }
    this.updateInvoice(invoiceId, patch);
  }
}

export const store = new Store();

export function useCrmData(): CrmData {
  const [, tick] = useState(0);
  useEffect(() => store.subscribe(() => tick((t) => t + 1)), []);
  return store.getState();
}
